import React, { useState, useEffect } from 'react';
import { 
  Row, Col, Typography, Card, Table, Tag, Button, Modal, Form, Input, 
  Select, message, Space, InputNumber, Upload, Image, Alert 
} from 'antd';
import { 
  EditOutlined, CheckCircleOutlined, 
  UploadOutlined, EyeOutlined, RollbackOutlined, SaveOutlined
} from '@ant-design/icons';
import MainLayout from '../../layouts/MainLayout';
import { financialApi } from '../../utils/financialApi';
import { extractApiUserMessage } from '../../utils/api';
import { formatPriceToman } from '../../utils/formatPriceToman';
import PersianDatePicker from '../../components/PersianDatePicker';
import { formatDate, toJalali } from '../../utils/dateUtils';

const { Title, Text } = Typography;
const { Option } = Select;
const { TextArea } = Input;

const toLatinDigits = (value) => {
  if (value == null) return '';
  const persianDigits = ['۰', '۱', '۲', '۳', '۴', '۵', '۶', '۷', '۸', '۹'];
  const arabicDigits = ['٠', '١', '٢', '٣', '٤', '٥', '٦', '٧', '٨', '٩'];
  let result = String(value);
  for (let i = 0; i < 10; i += 1) {
    result = result.replace(new RegExp(persianDigits[i], 'g'), String(i));
    result = result.replace(new RegExp(arabicDigits[i], 'g'), String(i));
  }
  return result;
};

const amountToPersianWords = (n) => {
  if (!n || isNaN(n)) return '';
  n = Math.floor(Number(n));
  if (n === 0) return 'صفر تومان';
  const units = ['', 'یک', 'دو', 'سه', 'چهار', 'پنج', 'شش', 'هفت', 'هشت', 'نه'];
  const teens = ['ده', 'یازده', 'دوازده', 'سیزده', 'چهارده', 'پانزده', 'شانزده', 'هفده', 'هجده', 'نوزده'];
  const tens = ['', 'ده', 'بیست', 'سی', 'چهل', 'پنجاه', 'شصت', 'هفتاد', 'هشتاد', 'نود'];
  const hundreds = ['', 'یکصد', 'دویست', 'سیصد', 'چهارصد', 'پانصد', 'ششصد', 'هفتصد', 'هشتصد', 'نهصد'];
  const scales = ['', 'هزار', 'میلیون', 'میلیارد', 'تریلیون'];

  function chunkToWords(num) {
    let parts = [];
    const h = Math.floor(num / 100);
    const rem = num % 100;
    if (h > 0) parts.push(hundreds[h]);
    if (rem >= 10 && rem <= 19) {
      parts.push(teens[rem - 10]);
    } else {
      const t = Math.floor(rem / 10);
      const u = rem % 10;
      if (t > 0) parts.push(tens[t]);
      if (u > 0) parts.push(units[u]);
    }
    return parts.join(' و ');
  }

  let chunks = [];
  let temp = n;
  while (temp > 0) {
    chunks.push(temp % 1000);
    temp = Math.floor(temp / 1000);
  }

  let resParts = [];
  for (let i = chunks.length - 1; i >= 0; i--) {
    const chunk = chunks[i];
    if (chunk > 0) {
      const words = chunkToWords(chunk);
      const scale = scales[i];
      resParts.push(scale ? words + ' ' + scale : words);
    }
  }
  return resParts.join(' و ') + ' تومان';
};

const PAYMENT_METHODS = {
  card_transfer: { text: 'کارت به کارت', color: 'blue' },
  bank_slip: { text: 'فیش بانکی', color: 'cyan' },
  satna_paya: { text: 'انتقال ساتنا / پایا', color: 'geekblue' },
  pos: { text: 'دستگاه کارتخوان (POS)', color: 'purple' },
  cash: { text: 'نقدی', color: 'green' },
  cheque: { text: 'چک صیادی', color: 'orange' },
  other: { text: 'سایر روش‌ها', color: 'default' },
};

const PAYMENT_PURPOSES = {
  deal_full: 'تسویه کامل معامله',
  deal_partial: 'پرداخت علی‌الحساب / مرحله‌ای',
  installment: 'پرداخت قسط',
  prepayment: 'پیش‌پرداخت',
  deposit: 'بیعانه / ودیعه',
  other: 'سایر موارد',
};

const Approvals = () => {
  const [approvals, setApprovals] = useState([]);
  const [stats, setStats] = useState(null);
  const [bankAccounts, setBankAccounts] = useState([]);
  const [loading, setLoading] = useState(true);

  // Decision & Review Modal
  const [decideModalVisible, setDecideModalVisible] = useState(false);
  const [decideLoading, setDecideLoading] = useState(false);
  const [currentPayment, setCurrentPayment] = useState(null);
  const [form] = Form.useForm();

  // Edit Mode inside Decide Modal
  const [isEditing, setIsEditing] = useState(false);
  const [editLoading, setEditLoading] = useState(false);
  const [editForm] = Form.useForm();
  const [editAmount, setEditAmount] = useState(0);
  const [receiptFileList, setReceiptFileList] = useState([]);

  // Match Web Order Modal
  const [matchModalVisible, setMatchModalVisible] = useState(false);
  const [matchLoading, setMatchLoading] = useState(false);
  const [matchForm] = Form.useForm();

  const fetchApprovals = async () => {
    try {
      setLoading(true);
      const res = await financialApi.getPendingApprovals();
      const data = res?.data?.approvals?.data || res?.data?.approvals || res?.data?.pending_payments || res?.approvals?.data || res?.approvals || [];
      setApprovals(Array.isArray(data) ? data : []);
      if (res?.data?.stats) {
        setStats(res.data.stats);
      }
    } catch (error) {
      console.error('Failed to fetch approvals:', error);
      message.error(extractApiUserMessage(error) || 'خطا در بارگذاری واریزی‌های در انتظار تأیید');
    } finally {
      setLoading(false);
    }
  };

  const fetchBankAccounts = async () => {
    try {
      const res = await financialApi.getChequeFormData();
      if (res?.data?.bank_accounts) {
        setBankAccounts(res.data.bank_accounts);
      }
    } catch (error) {
      console.error('Failed to fetch bank accounts:', error);
    }
  };

  useEffect(() => {
    fetchApprovals();
    fetchBankAccounts();
  }, []);

  const openDecideModal = (record, startInEditMode = false) => {
    setCurrentPayment(record);
    setIsEditing(startInEditMode);
    setReceiptFileList([]);
    const amt = Number(record.amount) || 0;
    setEditAmount(amt);

    // Initial values for Decision Form
    form.setFieldsValue({
      decision: 'approved',
      bank_account_id: record.bank_account_id ? Number(record.bank_account_id) : undefined,
      reason: '',
      rejection_reason: '',
    });

    // Initial values for Edit Form
    editForm.setFieldsValue({
      amount: amt,
      tracking_code: record.tracking_code || record.transaction_id || '',
      payment_method_type: record.payment_method_type || record.gateway || 'card_transfer',
      bank_account_id: record.bank_account_id ? Number(record.bank_account_id) : undefined,
      paid_on: record.paid_on ? (toJalali(record.paid_on) || record.paid_on) : null,
      payment_purpose: record.payment_purpose || 'deal_partial',
      remarks: record.remarks || '',
    });

    setDecideModalVisible(true);
  };

  const handleDecideSubmit = async (values) => {
    if (!currentPayment) return;
    try {
      setDecideLoading(true);
      const payload = {
        decision: values.decision,
        reason: values.reason || values.rejection_reason || undefined,
        bank_account_id: values.bank_account_id ? Number(values.bank_account_id) : undefined,
      };

      await financialApi.decidePayment(currentPayment.id, payload);
      message.success('وضعیت پرداخت با موفقیت ثبت شد.');
      setDecideModalVisible(false);
      form.resetFields();
      fetchApprovals();
    } catch (error) {
      message.error(extractApiUserMessage(error) || 'خطا در ثبت وضعیت');
    } finally {
      setDecideLoading(false);
    }
  };

  const handleEditSubmit = async (values) => {
    if (!currentPayment) return;
    try {
      setEditLoading(true);
      const formData = new FormData();
      formData.append('amount', Number(toLatinDigits(values.amount)));

      if (values.paid_on) {
        const paidOnGregorian = formatDate(values.paid_on, 'YYYY-MM-DD', 'gregorian') || String(values.paid_on);
        formData.append('paid_on', paidOnGregorian);
      }

      if (values.tracking_code) {
        formData.append('tracking_code', toLatinDigits(values.tracking_code).trim());
      } else {
        formData.append('tracking_code', '');
      }

      if (values.payment_method_type) {
        formData.append('payment_method_type', values.payment_method_type);
      }

      if (values.bank_account_id) {
        formData.append('bank_account_id', Number(values.bank_account_id));
      }

      if (values.payment_purpose) {
        formData.append('payment_purpose', values.payment_purpose);
      }

      if (values.remarks) {
        formData.append('remarks', values.remarks);
      } else {
        formData.append('remarks', '');
      }

      if (receiptFileList.length > 0 && receiptFileList[0].originFileObj) {
        formData.append('receipt_file', receiptFileList[0].originFileObj);
      }

      const res = await financialApi.updateApprovalPayment(currentPayment.id, formData);
      message.success('مشخصات واریزی با موفقیت ویرایش و ذخیره شد.');

      const updated = res?.data?.payment || res?.payment;
      if (updated) {
        setCurrentPayment(updated);
        // Also update decision bank account default
        form.setFieldsValue({
          bank_account_id: updated.bank_account_id ? Number(updated.bank_account_id) : undefined,
        });
      }

      setIsEditing(false);
      setReceiptFileList([]);
      fetchApprovals();
    } catch (error) {
      console.error('Failed to update payment:', error);
      message.error(extractApiUserMessage(error) || 'خطا در ویرایش مشخصات واریزی');
    } finally {
      setEditLoading(false);
    }
  };

  const handleMatchSubmit = async (values) => {
    try {
      setMatchLoading(true);
      const payload = {
        ...values,
        order_id: Number(values.order_id),
        amount: Number(values.amount),
      };
      await financialApi.matchWebOrder(payload);
      message.success('سفارش وب با موفقیت تطبیق داده شد.');
      setMatchModalVisible(false);
      matchForm.resetFields();
      fetchApprovals();
    } catch (error) {
      message.error(extractApiUserMessage(error) || 'خطا در تطبیق سفارش');
    } finally {
      setMatchLoading(false);
    }
  };

  const columns = [
    {
      title: 'شماره پیگیری',
      dataIndex: 'tracking_code',
      key: 'tracking_code',
      render: (text, record) => (
        <div className="font-mono text-xs">
          <Text className="font-semibold text-gray-800 dark:text-gray-200">
            {text || record.transaction_id || '-'}
          </Text>
        </div>
      ),
    },
    {
      title: 'معامله و مشتری',
      dataIndex: ['deal', 'name'],
      key: 'deal_name',
      render: (dealName, record) => (
        <div>
          <div className="font-semibold text-gray-800 dark:text-gray-100 text-xs sm:text-sm">
            {dealName || (record.deal ? `معامله #${record.deal.id}` : '-')}
          </div>
          {record.deal?.client && (
            <div className="text-[11px] text-gray-400 mt-0.5">
              مشتری: {record.deal.client.name} {record.deal.client.mobile ? `(${record.deal.client.mobile})` : ''}
            </div>
          )}
        </div>
      ),
    },
    {
      title: 'مبلغ (تومان)',
      dataIndex: 'amount',
      key: 'amount',
      render: (amount) => (
        <div className="font-semibold text-emerald-600 dark:text-emerald-400">
          {formatPriceToman(amount || 0)}
        </div>
      ),
    },
    {
      title: 'روش پرداخت',
      dataIndex: 'payment_method_type',
      key: 'payment_method_type',
      render: (method, record) => {
        const val = method || record.gateway || 'card_transfer';
        const cfg = PAYMENT_METHODS[val] || { text: val, color: 'default' };
        return <Tag color={cfg.color}>{cfg.text}</Tag>;
      },
    },
    {
      title: 'تاریخ پرداخت',
      dataIndex: 'paid_on',
      key: 'paid_on',
      render: (date) => (
        <span dir="ltr" className="text-xs text-gray-600 dark:text-gray-300 font-mono">
          {date ? (toJalali(date) || '-') : '-'}
        </span>
      ),
    },
    {
      title: 'وضعیت',
      dataIndex: 'approval_status',
      key: 'approval_status',
      render: (status) => {
        if (status === 'needs_correction') {
          return <Tag color="error">نیازمند اصلاح</Tag>;
        }
        return <Tag color="warning">در انتظار تایید</Tag>;
      },
    },
    {
      title: 'فایل فیش',
      dataIndex: 'receipt_file',
      key: 'receipt_file',
      render: (file, record) => {
        const url = record?.receipt_file_url || (file ? `/user-uploads/payment-receipts/${file}` : null);
        return url ? (
          <a 
            href={url} 
            target="_blank" 
            rel="noreferrer" 
            className="text-blue-600 hover:text-blue-800 font-medium text-xs flex items-center gap-1"
          >
            <EyeOutlined /> مشاهده فیش
          </a>
        ) : (
          <span className="text-gray-400 text-xs">ندارد</span>
        );
      },
    },
    {
      title: 'عملیات',
      key: 'action',
      render: (_, record) => (
        <Space size="small">
          <Button 
            type="primary" 
            size="small" 
            onClick={() => openDecideModal(record, false)}
            style={{ fontFamily: 'CustomFont' }}
          >
            بررسی و تصمیم‌گیری
          </Button>
          <Button 
            size="small" 
            icon={<EditOutlined className="text-blue-600" />} 
            onClick={() => openDecideModal(record, true)}
            style={{ fontFamily: 'CustomFont' }}
          >
            ویرایش
          </Button>
        </Space>
      ),
    },
  ];

  const receiptUrl = currentPayment?.receipt_file_url || (currentPayment?.receipt_file ? `/user-uploads/payment-receipts/${currentPayment.receipt_file}` : null);

  return (
    <MainLayout>
      <div className="p-4 md:p-6 pb-24 max-w-[1600px] mx-auto animate-fade-in" style={{ fontFamily: 'CustomFont' }}>
        <div className="mb-6 flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
          <div>
            <Title level={3} className="m-0 text-gray-800 dark:text-gray-100 font-bold" style={{ fontFamily: 'CustomFont' }}>
              کارتابل تایید واریزی‌ها
            </Title>
            <Text className="text-gray-500 dark:text-gray-400" style={{ fontFamily: 'CustomFont' }}>
              بررسی، تایید یا رد واریزی‌های معامله و تطبیق سفارشات وب
            </Text>
          </div>
          <Button 
            type="primary" 
            onClick={() => setMatchModalVisible(true)}
            style={{ fontFamily: 'CustomFont' }}
          >
            تطبیق سفارش وب
          </Button>
        </div>

        {stats && (
          <Row gutter={[16, 16]} className="mb-6">
            <Col xs={24} sm={8}>
              <Card className="shadow-sm rounded-2xl dark:bg-gray-800">
                <Text className="text-gray-500 block mb-1">تعداد در انتظار تایید</Text>
                <Title level={4} className="m-0 text-amber-500">{stats.total_pending_count || 0}</Title>
              </Card>
            </Col>
            <Col xs={24} sm={8}>
              <Card className="shadow-sm rounded-2xl dark:bg-gray-800">
                <Text className="text-gray-500 block mb-1">مبلغ کل در انتظار تایید</Text>
                <Title level={4} className="m-0 text-blue-500">{formatPriceToman(stats.total_pending_amount || 0)}</Title>
              </Card>
            </Col>
            <Col xs={24} sm={8}>
              <Card className="shadow-sm rounded-2xl dark:bg-gray-800">
                <Text className="text-gray-500 block mb-1">نیازمند اصلاح</Text>
                <Title level={4} className="m-0 text-red-500">{stats.needs_correction_count || 0}</Title>
              </Card>
            </Col>
          </Row>
        )}

        <Card 
          loading={loading}
          bordered={false} 
          className="shadow-sm dark:bg-gray-800 rounded-2xl h-full"
        >
          <Table 
            dataSource={approvals} 
            columns={columns}
            rowKey="id"
            pagination={false}
            size="middle"
            className="custom-table"
            locale={{ emptyText: 'واریزی در انتظار تایید یافت نشد' }}
          />
        </Card>

        {/* Decide & Review Modal */}
        <Modal
          title={
            <div className="flex items-center justify-between pl-6 text-sm sm:text-base font-bold" style={{ fontFamily: 'CustomFont' }}>
              <div className="flex items-center gap-2">
                <CheckCircleOutlined className="text-blue-600" />
                <span>{isEditing ? 'ویرایش اطلاعات واریزی' : 'بررسی و تصمیم‌گیری واریزی'}</span>
                {currentPayment && (
                  <Tag color="blue" className="font-mono text-xs">
                    پرداخت #{currentPayment.id}
                  </Tag>
                )}
              </div>
              {!isEditing && (
                <Button
                  type="default"
                  size="small"
                  icon={<EditOutlined className="text-blue-600" />}
                  onClick={() => setIsEditing(true)}
                  className="flex items-center gap-1 border-blue-200 text-blue-600 hover:text-blue-700"
                  style={{ fontFamily: 'CustomFont' }}
                >
                  ویرایش اطلاعات
                </Button>
              )}
            </div>
          }
          open={decideModalVisible}
          onCancel={() => {
            setDecideModalVisible(false);
            setIsEditing(false);
          }}
          footer={null}
          width={720}
          destroyOnClose
        >
          {currentPayment && (
            <div className="pt-2" style={{ fontFamily: 'CustomFont' }}>
              {!isEditing ? (
                <>
                  {/* Review Card */}
                  <div className="p-4 bg-gray-50 dark:bg-gray-800/80 rounded-2xl border border-gray-200 dark:border-gray-700 mb-5">
                    <Row gutter={[16, 14]}>
                      <Col span={12}>
                        <div className="text-xs text-gray-400 mb-0.5">معامله مرتبط:</div>
                        <div className="font-bold text-sm text-gray-800 dark:text-gray-100">
                          {currentPayment.deal?.name || (currentPayment.deal ? `معامله #${currentPayment.deal.id}` : '-')}
                        </div>
                      </Col>

                      <Col span={12}>
                        <div className="text-xs text-gray-400 mb-0.5">مشتری:</div>
                        <div className="font-semibold text-sm text-gray-800 dark:text-gray-200">
                          {currentPayment.deal?.client?.name || currentPayment.customer?.name || '-'}
                          {currentPayment.deal?.client?.mobile && (
                            <span className="text-xs text-gray-400 mr-1.5 font-mono">
                              ({currentPayment.deal.client.mobile})
                            </span>
                          )}
                        </div>
                      </Col>

                      <Col span={12}>
                        <div className="text-xs text-gray-400 mb-0.5">مبلغ واریزی:</div>
                        <div className="font-bold text-base text-emerald-600 dark:text-emerald-400">
                          {formatPriceToman(currentPayment.amount || 0)}
                        </div>
                        <div className="text-[11px] text-gray-500 dark:text-gray-400 mt-0.5">
                          {amountToPersianWords(currentPayment.amount)}
                        </div>
                      </Col>

                      <Col span={12}>
                        <div className="text-xs text-gray-400 mb-0.5">تاریخ پرداخت:</div>
                        <div className="font-semibold text-sm text-gray-800 dark:text-gray-200" dir="ltr">
                          {currentPayment.paid_on ? toJalali(currentPayment.paid_on) : '-'}
                        </div>
                      </Col>

                      <Col span={12}>
                        <div className="text-xs text-gray-400 mb-0.5">شماره پیگیری / رهگیری:</div>
                        <div className="font-mono font-bold text-sm text-blue-600 dark:text-blue-400">
                          {currentPayment.tracking_code || currentPayment.transaction_id || '-'}
                        </div>
                      </Col>

                      <Col span={12}>
                        <div className="text-xs text-gray-400 mb-0.5">روش پرداخت:</div>
                        <div>
                          {(() => {
                            const val = currentPayment.payment_method_type || currentPayment.gateway || 'card_transfer';
                            const cfg = PAYMENT_METHODS[val] || { text: val, color: 'default' };
                            return <Tag color={cfg.color}>{cfg.text}</Tag>;
                          })()}
                        </div>
                      </Col>

                      <Col span={12}>
                        <div className="text-xs text-gray-400 mb-0.5">حساب بانکی مقصد:</div>
                        <div className="text-xs font-semibold text-gray-700 dark:text-gray-300">
                          {currentPayment.bank_account ? (
                            <span>
                              {currentPayment.bank_account.bank_name} 
                              {currentPayment.bank_account.account_number ? ` - ${currentPayment.bank_account.account_number}` : ''}
                            </span>
                          ) : (
                            <span className="text-amber-600">تعیین نشده (هنگام تایید انتخاب کنید)</span>
                          )}
                        </div>
                      </Col>

                      <Col span={12}>
                        <div className="text-xs text-gray-400 mb-0.5">کارشناس ثبت‌کننده:</div>
                        <div className="text-xs font-semibold text-gray-700 dark:text-gray-300">
                          {currentPayment.sales_owner?.name || currentPayment.added_by || '-'}
                        </div>
                      </Col>

                      <Col span={24}>
                        <div className="text-xs text-gray-400 mb-0.5">بابت / شرح پرداخت:</div>
                        <div className="text-xs text-gray-700 dark:text-gray-300">
                          <span className="font-semibold text-gray-800 dark:text-gray-200">
                            {PAYMENT_PURPOSES[currentPayment.payment_purpose] || currentPayment.payment_purpose || 'پرداخت معامله'}: 
                          </span>{' '}
                          {currentPayment.remarks || 'توضیحات تکمیلی ثبت نشده است.'}
                        </div>
                      </Col>

                      {currentPayment.next_installment_amount && currentPayment.next_installment_date && (
                        <Col span={24}>
                          <div className="text-xs bg-amber-50 dark:bg-amber-950/20 border border-amber-200 dark:border-amber-900/50 rounded-lg px-3 py-2 text-amber-800 dark:text-amber-300">
                            <span className="font-semibold">قسط بعدی اعلام‌شده: </span>
                            {formatPriceToman(currentPayment.next_installment_amount)} تا تاریخ تقریبی {toJalali(currentPayment.next_installment_date)}
                            <div className="text-[11px] opacity-80 mt-0.5">با تایید این واریزی، این مبلغ به بدهی‌های معامله اضافه می‌شود.</div>
                          </div>
                        </Col>
                      )}

                      {receiptUrl && (
                        <Col span={24}>
                          <div className="text-xs text-gray-400 mb-1">فیش واریزی:</div>
                          <div className="flex items-center gap-3 p-2 bg-white dark:bg-gray-700 rounded-xl border border-gray-100 dark:border-gray-600">
                            <Image
                              src={receiptUrl}
                              alt="تصویر فیش"
                              width={48}
                              height={48}
                              className="rounded-lg object-cover"
                              fallback="https://placehold.co/100x100?text=Receipt"
                            />
                            <div className="flex-1">
                              <a
                                href={receiptUrl}
                                target="_blank"
                                rel="noreferrer"
                                className="text-blue-600 hover:text-blue-800 font-medium text-xs flex items-center gap-1"
                              >
                                <EyeOutlined /> مشاهده فایل کامل فیش واریزی
                              </a>
                            </div>
                          </div>
                        </Col>
                      )}
                    </Row>
                  </div>

                  {/* Decision Form */}
                  <div className="p-4 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-2xl">
                    <div className="font-bold text-sm text-gray-800 dark:text-gray-100 mb-3 flex items-center gap-1.5">
                      <CheckCircleOutlined className="text-emerald-500" />
                      <span>ثبت تصمیم مدیر مالی</span>
                    </div>

                    <Form form={form} layout="vertical" onFinish={handleDecideSubmit}>
                      <Form.Item 
                        name="decision" 
                        label="تصمیم‌گیری" 
                        rules={[{ required: true, message: 'انتخاب تصمیم الزامی است' }]}
                      >
                        <Select placeholder="تصمیم خود را انتخاب کنید" size="large">
                          <Option value="approved">
                            <span className="text-emerald-600 font-bold">✓ تایید واریزی و ثبت در حسابداری</span>
                          </Option>
                          <Option value="needs_correction">
                            <span className="text-amber-600 font-bold">⚠ نیاز به اصلاح توسط مشاور</span>
                          </Option>
                          <Option value="rejected">
                            <span className="text-red-600 font-bold">✕ رد واریزی</span>
                          </Option>
                        </Select>
                      </Form.Item>

                      <Form.Item
                        noStyle
                        shouldUpdate={(prevValues, currentValues) => prevValues.decision !== currentValues.decision}
                      >
                        {({ getFieldValue }) => {
                          const decision = getFieldValue('decision');
                          if (decision === 'approved') {
                            return (
                              <Form.Item 
                                name="bank_account_id" 
                                label="حساب بانکی مقصد جهت واریز وجه" 
                                rules={[{ required: true, message: 'انتخاب حساب بانکی الزامی است' }]}
                              >
                                <Select placeholder="انتخاب حساب بانکی">
                                  {bankAccounts.map((b) => (
                                    <Option key={b.id} value={b.id}>
                                      {b.bank_name} {b.account_number ? `- ${b.account_number}` : ''}
                                    </Option>
                                  ))}
                                </Select>
                              </Form.Item>
                            );
                          }
                          if (decision === 'rejected' || decision === 'needs_correction') {
                            return (
                              <Form.Item 
                                name="reason" 
                                label={decision === 'rejected' ? 'دلیل رد واریزی' : 'شرح موارد نیازمند اصلاح توسط مشاور'} 
                                rules={[{ required: true, message: 'درج توضیحات الزامی است' }]}
                              >
                                <TextArea 
                                  rows={3} 
                                  placeholder="علت یا توضیحات را برای مشاور بنویسید..." 
                                />
                              </Form.Item>
                            );
                          }
                          return null;
                        }}
                      </Form.Item>

                      <div className="flex justify-between items-center pt-3 border-t border-gray-100 dark:border-gray-700 mt-4">
                        <Button
                          icon={<EditOutlined className="text-blue-600" />}
                          onClick={() => setIsEditing(true)}
                          className="text-blue-600"
                        >
                          نیاز به ویرایش اطلاعات دارم
                        </Button>
                        <Space>
                          <Button onClick={() => setDecideModalVisible(false)}>انصراف</Button>
                          <Button type="primary" htmlType="submit" loading={decideLoading} className="bg-emerald-600 hover:bg-emerald-700">
                            ثبت نهایی تصمیم
                          </Button>
                        </Space>
                      </div>
                    </Form>
                  </div>
                </>
              ) : (
                /* Edit Mode Form */
                <div className="p-4 bg-white dark:bg-gray-800 border border-blue-200 dark:border-blue-900 rounded-2xl">
                  <div className="flex items-center justify-between mb-4 pb-3 border-b border-gray-100 dark:border-gray-700">
                    <div className="flex items-center gap-2 text-blue-600 font-bold text-sm">
                      <EditOutlined />
                      <span>فرم ویرایش مشخصات واریزی</span>
                    </div>
                    <Button 
                      size="small" 
                      icon={<RollbackOutlined />} 
                      onClick={() => setIsEditing(false)}
                    >
                      بازگشت به بررسی
                    </Button>
                  </div>

                  <Alert
                    message="توجه: تغییرات بلافاصله پس از ذخیره روی رکورد پرداخت اعمال شده و در لاگ مالی ثبت می‌گردد."
                    type="info"
                    showIcon
                    className="mb-4 text-xs"
                  />

                  <Form 
                    form={editForm} 
                    layout="vertical" 
                    onFinish={handleEditSubmit}
                    initialValues={{
                      amount: currentPayment.amount,
                      tracking_code: currentPayment.tracking_code || '',
                      payment_method_type: currentPayment.payment_method_type || currentPayment.gateway || 'card_transfer',
                      bank_account_id: currentPayment.bank_account_id ? Number(currentPayment.bank_account_id) : undefined,
                      paid_on: currentPayment.paid_on ? (toJalali(currentPayment.paid_on) || currentPayment.paid_on) : null,
                      payment_purpose: currentPayment.payment_purpose || 'deal_partial',
                      remarks: currentPayment.remarks || '',
                    }}
                  >
                    <Row gutter={16}>
                      <Col span={12}>
                        <Form.Item 
                          name="amount" 
                          label="مبلغ واریزی (تومان)" 
                          rules={[{ required: true, message: 'مبلغ واریزی الزامی است' }]}
                          extra={
                            editAmount > 0 && (
                              <div className="text-xs text-blue-600 dark:text-blue-400 font-semibold mt-1">
                                {amountToPersianWords(editAmount)}
                              </div>
                            )
                          }
                        >
                          <InputNumber
                            className="w-full"
                            size="large"
                            formatter={(value) => `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
                            parser={(value) => value ? value.replace(/\$\s?|(,*)/g, '') : ''}
                            onChange={(val) => setEditAmount(Number(toLatinDigits(val)) || 0)}
                            placeholder="مبلغ به تومان"
                          />
                        </Form.Item>
                      </Col>

                      <Col span={12}>
                        <Form.Item 
                          name="paid_on" 
                          label="تاریخ پرداخت" 
                          rules={[{ required: true, message: 'تاریخ پرداخت الزامی است' }]}
                        >
                          <PersianDatePicker 
                            placeholder="انتخاب تاریخ پرداخت"
                            style={{ width: '100%' }}
                          />
                        </Form.Item>
                      </Col>

                      <Col span={12}>
                        <Form.Item 
                          name="payment_method_type" 
                          label="روش پرداخت"
                          rules={[{ required: true, message: 'انتخاب روش پرداخت الزامی است' }]}
                        >
                          <Select placeholder="انتخاب روش پرداخت">
                            {Object.entries(PAYMENT_METHODS).map(([key, item]) => (
                              <Option key={key} value={key}>{item.text}</Option>
                            ))}
                          </Select>
                        </Form.Item>
                      </Col>

                      <Col span={12}>
                        <Form.Item 
                          name="tracking_code" 
                          label="شماره پیگیری / شناسه تراکنش"
                        >
                          <Input placeholder="شماره پیگیری فیش یا تراکنش" />
                        </Form.Item>
                      </Col>

                      <Col span={12}>
                        <Form.Item 
                          name="bank_account_id" 
                          label="حساب بانکی شرکت"
                        >
                          <Select placeholder="انتخاب حساب بانکی شرکت" allowClear>
                            {bankAccounts.map((b) => (
                              <Option key={b.id} value={b.id}>
                                {b.bank_name} {b.account_number ? `- ${b.account_number}` : ''}
                              </Option>
                            ))}
                          </Select>
                        </Form.Item>
                      </Col>

                      <Col span={12}>
                        <Form.Item 
                          name="payment_purpose" 
                          label="بابت پرداخت"
                        >
                          <Select placeholder="انتخاب بابت">
                            {Object.entries(PAYMENT_PURPOSES).map(([key, label]) => (
                              <Option key={key} value={key}>{label}</Option>
                            ))}
                          </Select>
                        </Form.Item>
                      </Col>

                      <Col span={24}>
                        <Form.Item name="remarks" label="توضیحات و یادداشت">
                          <TextArea rows={2} placeholder="توضیحات مربوط به واریزی..." />
                        </Form.Item>
                      </Col>

                      <Col span={24}>
                        <Form.Item label="فایل فیش واریزی جدید (اختیاری جهت تعویض فیش)">
                          <Upload
                            beforeUpload={() => false}
                            maxCount={1}
                            fileList={receiptFileList}
                            onChange={({ fileList }) => setReceiptFileList(fileList)}
                          >
                            <Button icon={<UploadOutlined />}>انتخاب فایل تصویر یا PDF فیش</Button>
                          </Upload>
                          {receiptUrl && receiptFileList.length === 0 && (
                            <div className="text-[11px] text-gray-500 mt-1">
                              فیش فعلی: <a href={receiptUrl} target="_blank" rel="noreferrer" className="text-blue-500">مشاهده فیش موجود</a>
                            </div>
                          )}
                        </Form.Item>
                      </Col>
                    </Row>

                    <div className="flex justify-end gap-2 pt-3 border-t border-gray-100 dark:border-gray-700 mt-2">
                      <Button onClick={() => setIsEditing(false)}>انصراف</Button>
                      <Button 
                        type="primary" 
                        htmlType="submit" 
                        icon={<SaveOutlined />} 
                        loading={editLoading}
                        className="bg-blue-600 hover:bg-blue-700"
                      >
                        ذخیره تغییرات
                      </Button>
                    </div>
                  </Form>
                </div>
              )}
            </div>
          )}
        </Modal>

        {/* Match Web Order Modal */}
        <Modal
          title="تطبیق خودکار سفارش وب"
          open={matchModalVisible}
          onCancel={() => setMatchModalVisible(false)}
          footer={null}
        >
          <Form form={matchForm} layout="vertical" onFinish={handleMatchSubmit}>
            <Form.Item name="order_id" label="شناسه سفارش (Order ID)" rules={[{ required: true }]}>
              <Input type="number" />
            </Form.Item>
            <Form.Item name="customer_phone" label="موبایل مشتری" rules={[{ required: true }]}>
              <Input />
            </Form.Item>
            <Form.Item name="amount" label="مبلغ پرداخت شده" rules={[{ required: true }]}>
              <Input type="number" />
            </Form.Item>
            <Form.Item name="gateway" label="درگاه" rules={[{ required: true }]}>
              <Input placeholder="مانند: Zarinpal" />
            </Form.Item>
            <Form.Item name="transaction_id" label="شناسه تراکنش" rules={[{ required: true }]}>
              <Input />
            </Form.Item>

            <Form.Item className="mb-0 flex justify-end">
              <Space>
                <Button onClick={() => setMatchModalVisible(false)}>انصراف</Button>
                <Button type="primary" htmlType="submit" loading={matchLoading}>تطبیق</Button>
              </Space>
            </Form.Item>
          </Form>
        </Modal>
      </div>
    </MainLayout>
  );
};

export default Approvals;
